import React, { useEffect, useId, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { btnPrimaryClass, btnSecondaryClass, formControlClass } from "../dashboardStyles";
import { EMBED_OPEN_ATTRIBUTE, buildEmbedSnippet, normalizeHex } from "../../../utils/embed";
import { Field, PanelCard, labelTextClass } from "./site/siteFields";
import WidgetPreview from "./site/WidgetPreview";

const codeBlockClass =
  "m-0 overflow-x-auto rounded-lg border border-ink-200 bg-ink-50 p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-all text-ink-800";

const hintClass = "mt-1.5 mb-0 text-xs text-ink-500";

const MODES = ["popup", "inline"];

/** A tiny browser with the widget in it, the way each mode puts it there. */
const ModeIllustration = ({ mode, color }) => (
  <span className="block overflow-hidden rounded-md border border-ink-200 bg-white" aria-hidden="true">
    <span className="flex gap-0.5 border-b border-ink-100 bg-ink-50 px-1.5 py-1">
      <span className="size-1 rounded-full bg-red-400" />
      <span className="size-1 rounded-full bg-amber-400" />
      <span className="size-1 rounded-full bg-green-500" />
    </span>
    <span className="relative flex h-16 gap-2 p-2">
      <span className="flex flex-1 flex-col gap-1.5">
        <span className="h-3 w-4/5 rounded-sm bg-ink-100" />
        <span className="h-1.5 w-3/5 rounded-full bg-ink-100" />
        <span className="h-1.5 w-2/5 rounded-full bg-ink-100" />
      </span>
      {mode === "inline" ? (
        <span className="flex w-2/5 flex-col gap-1 rounded border border-ink-200 p-1">
          <span className="h-1 w-full rounded-full" style={{ backgroundColor: color }} />
          <span className="h-1.5 w-4/5 rounded-full bg-ink-100" />
          <span className="h-1.5 w-3/5 rounded-full bg-ink-100" />
          <span className="h-1.5 w-4/5 rounded-full bg-ink-100" />
        </span>
      ) : (
        <span
          className="absolute right-2 bottom-2 flex h-3 w-9 items-center justify-center rounded-full"
          style={{ backgroundColor: color }}
        >
          <span className="size-1 rounded-full bg-white" />
        </span>
      )}
    </span>
  </span>
);

ModeIllustration.propTypes = {
  mode: PropTypes.oneOf(MODES).isRequired,
  color: PropTypes.string.isRequired,
};

const radioDotClass = (checked) =>
  `flex size-5 shrink-0 items-center justify-center rounded-full border-2 ${
    checked ? "border-brand-600 bg-brand-600 text-white" : "border-ink-300 bg-white"
  }`;

/**
 * The Widget tab of Site & widget — for businesses that already have a site
 * and want the booking form on it rather than a second site of ours.
 *
 * How it appears (a button and a window, or the form in the page), the
 * button's text, and optionally the business's own button instead of ours;
 * beside it the real widget on a sketch of a site, and under it the code. That
 * code is the one thing to take away, so "Copy code" is the strongest button
 * on the page and works without the code ever being shown.
 *
 * Nothing here is saved: the snippet is derived from the slug, the mode, the
 * button and the colour, and the colour is the Site tab's (`onChangeColor`
 * goes there).
 */
const EmbedWidgetCard = ({ slug, primaryColor, savedColor = null, defaultLabel, onChangeColor = null }) => {
  const { t } = useTranslation();
  const ids = useId();
  const [mode, setMode] = useState("popup");
  const [label, setLabel] = useState(
    defaultLabel || t("Dashboard.MySite.widget.buttonTextDefault"),
  );
  const [ownButton, setOwnButton] = useState(false);
  const [ownButtonOpen, setOwnButtonOpen] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [copied, setCopied] = useState(null); // "code" | "link" | "attribute" | "failed"
  const copiedTimer = useRef(null);

  const origin = window.location.origin;
  const popup = mode === "popup";
  const hex = normalizeHex(primaryColor) || "#5c9f24";
  const buttonLabel = label.trim() || t("Dashboard.MySite.widget.buttonTextDefault");
  const snippet = buildEmbedSnippet({
    origin,
    slug,
    mode,
    label: buttonLabel,
    color: primaryColor,
    ownButton: popup && ownButton,
  });
  const directLink = `${origin}/${slug}`;
  const colorPending = Boolean(savedColor) && normalizeHex(savedColor) !== normalizeHex(primaryColor);

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const copy = async (text, what) => {
    clearTimeout(copiedTimer.current);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(what);
      copiedTimer.current = setTimeout(() => setCopied(null), 3000);
    } catch {
      setCopied("failed");
      // Copying by hand needs the code on screen.
      if (what === "code") setShowCode(true);
    }
  };

  const change = (setter) => (value) => {
    setter(value);
    setCopied(null);
  };

  if (!slug) {
    return (
      <PanelCard title={t("Dashboard.MySite.widget.modeLabel")}>
        <p className="m-0 text-fluid-sm text-ink-500">{t("Dashboard.MySite.widget.noSlug")}</p>
      </PanelCard>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <PanelCard title={t("Dashboard.MySite.widget.modeLabel")}>
          <div
            role="radiogroup"
            aria-label={t("Dashboard.MySite.widget.modeLabel")}
            className="grid gap-3 sm:grid-cols-2"
          >
            {MODES.map((option) => {
              const checked = mode === option;
              return (
                <label
                  key={option}
                  className={`flex min-w-0 cursor-pointer flex-col gap-3 rounded-xl border-2 p-3 transition has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand-600 ${
                    checked ? "border-brand-600 bg-brand-50" : "border-ink-200 bg-white hover:border-ink-300"
                  }`}
                >
                  <input
                    type="radio"
                    name={`${ids}-mode`}
                    value={option}
                    checked={checked}
                    onChange={() => change(setMode)(option)}
                    aria-labelledby={`${ids}-${option}-title`}
                    aria-describedby={`${ids}-${option}-hint`}
                    className="sr-only"
                  />
                  <span className="flex items-center gap-2">
                    <span className={radioDotClass(checked)} aria-hidden="true">
                      {checked && <i className="bi bi-check text-sm leading-none" />}
                    </span>
                    <span id={`${ids}-${option}-title`} className="text-sm font-semibold text-ink-900">
                      {t(`Dashboard.MySite.widget.${option}`)}
                    </span>
                  </span>
                  <ModeIllustration mode={option} color={hex} />
                  <span id={`${ids}-${option}-hint`} className="text-xs text-ink-500">
                    {t(`Dashboard.MySite.widget.${option}Hint`)}
                  </span>
                </label>
              );
            })}
          </div>

          {popup && (
            <Field
              id="embed-label"
              label={t("Dashboard.MySite.widget.buttonText")}
              info={t("Dashboard.MySite.widget.buttonTextTip")}
            >
              <input
                type="text"
                id="embed-label"
                value={label}
                maxLength={40}
                onChange={(e) => change(setLabel)(e.target.value)}
                className={formControlClass}
              />
            </Field>
          )}

          <div className="min-w-0">
            <p className={`${labelTextClass} mt-0 mb-1.5`}>{t("Dashboard.MySite.widget.color")}</p>
            <div className="flex flex-wrap items-center gap-3">
              <span
                className="size-11 shrink-0 rounded-full border border-ink-200 shadow-card"
                style={{ backgroundColor: hex }}
                aria-hidden="true"
              />
              <span className="rounded-lg border border-ink-200 bg-white px-3 py-2.5 font-mono text-sm text-ink-800">
                {hex}
              </span>
              {onChangeColor && (
                <button
                  type="button"
                  onClick={onChangeColor}
                  className="inline-flex min-h-11 cursor-pointer items-center rounded-lg px-1 text-sm font-semibold text-brand-700 underline underline-offset-4 transition hover:text-brand-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600"
                >
                  {t("Dashboard.MySite.widget.changeColor")}
                </button>
              )}
            </div>
            <p className={hintClass}>{t("Dashboard.MySite.widget.colorFrom")}</p>
            {colorPending && (
              <p className="mt-1.5 mb-0 flex items-start gap-1.5 text-xs text-amber-700">
                <i className="bi bi-exclamation-circle mt-px" aria-hidden="true" />
                {t("Dashboard.MySite.widget.colorUnsaved")}
              </p>
            )}
          </div>

          {popup && (
            <div className="rounded-xl border border-ink-200">
              <button
                type="button"
                aria-expanded={ownButtonOpen}
                aria-controls={`${ids}-own`}
                onClick={() => setOwnButtonOpen((value) => !value)}
                className="flex min-h-14 w-full cursor-pointer items-center gap-3 rounded-xl px-4 py-3 text-left transition hover:bg-ink-50 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-brand-600"
              >
                <i
                  className={`bi bi-chevron-right text-ink-500 transition-transform motion-reduce:transition-none ${ownButtonOpen ? "rotate-90" : ""}`}
                  aria-hidden="true"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-ink-900">
                    {t("Dashboard.MySite.widget.own.title")}
                  </span>
                  <span className="block text-xs text-ink-500">{t("Dashboard.MySite.widget.own.subtitle")}</span>
                </span>
              </button>
              {ownButtonOpen && (
                <div id={`${ids}-own`} className="flex flex-col gap-4 border-t border-ink-200 px-4 py-4">
                  <div
                    role="radiogroup"
                    aria-label={t("Dashboard.MySite.widget.own.label")}
                    className="flex flex-wrap gap-x-6 gap-y-1"
                  >
                    {[false, true].map((mine) => (
                      <label
                        key={String(mine)}
                        className="inline-flex min-h-11 cursor-pointer items-center gap-2.5 rounded-lg text-sm font-medium text-ink-800 has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-brand-600"
                      >
                        <input
                          type="radio"
                          name={`${ids}-own`}
                          checked={ownButton === mine}
                          onChange={() => change(setOwnButton)(mine)}
                          className="sr-only"
                        />
                        <span
                          className={`flex size-5 items-center justify-center rounded-full border-2 ${
                            ownButton === mine ? "border-brand-600" : "border-ink-300"
                          }`}
                          aria-hidden="true"
                        >
                          {ownButton === mine && <span className="size-2.5 rounded-full bg-brand-600" />}
                        </span>
                        {t(`Dashboard.MySite.widget.own.${mine ? "mine" : "floating"}`)}
                      </label>
                    ))}
                  </div>
                  <p className="m-0 text-xs text-ink-500">
                    {t(`Dashboard.MySite.widget.own.${ownButton ? "mineHint" : "floatingHint"}`)}
                  </p>
                  {ownButton && (
                    <div className="min-w-0">
                      <label htmlFor={`${ids}-attribute`} className={`${labelTextClass} mb-1.5 block`}>
                        {t("Dashboard.MySite.widget.own.attribute")}
                      </label>
                      <div className="flex flex-col gap-2 sm:flex-row">
                        <input
                          id={`${ids}-attribute`}
                          type="text"
                          readOnly
                          value={EMBED_OPEN_ATTRIBUTE}
                          onFocus={(e) => e.target.select()}
                          className={`${formControlClass} font-mono`}
                        />
                        <button
                          type="button"
                          className={`${btnSecondaryClass} shrink-0`}
                          onClick={() => copy(EMBED_OPEN_ATTRIBUTE, "attribute")}
                        >
                          <i className={`bi ${copied === "attribute" ? "bi-check2" : "bi-copy"}`} aria-hidden="true" />
                          {copied === "attribute"
                            ? t("Dashboard.MySite.widget.copiedShort")
                            : t("Dashboard.MySite.widget.own.copyAttribute")}
                        </button>
                      </div>
                      <p className={hintClass}>{t("Dashboard.MySite.widget.own.attributeHint")}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="min-w-0">
            <label htmlFor={`${ids}-link`} className={`${labelTextClass} mb-1.5 block`}>
              {t("Dashboard.MySite.widget.link")}
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                id={`${ids}-link`}
                type="text"
                readOnly
                value={directLink.replace(/^https?:\/\//, "")}
                onFocus={(e) => e.target.select()}
                className={formControlClass}
              />
              <button
                type="button"
                className={`${btnSecondaryClass} shrink-0`}
                onClick={() => copy(directLink, "link")}
              >
                <i className={`bi ${copied === "link" ? "bi-check2" : "bi-copy"}`} aria-hidden="true" />
                {copied === "link"
                  ? t("Dashboard.MySite.widget.copiedShort")
                  : t("Dashboard.MySite.widget.copyLink")}
              </button>
            </div>
            <p className={hintClass}>{t("Dashboard.MySite.widget.linkHint")}</p>
          </div>
        </PanelCard>

        <WidgetPreview slug={slug} mode={mode} ownButton={popup && ownButton} label={buttonLabel} color={primaryColor} />
      </div>

      <section className="rounded-card border border-ink-200 bg-white shadow-card" aria-labelledby={`${ids}-code-title`}>
        <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="flex flex-wrap items-center gap-3">
            <h2 id={`${ids}-code-title`} className="m-0 flex items-center gap-2 text-lg font-semibold text-ink-900">
              <i className="bi bi-code-slash" aria-hidden="true" />
              {t("Dashboard.MySite.widget.code.title")}
            </h2>
            <button
              type="button"
              className={btnSecondaryClass}
              aria-expanded={showCode}
              aria-controls={`${ids}-code`}
              onClick={() => setShowCode((value) => !value)}
            >
              <i className="bi bi-code" aria-hidden="true" />
              {t(showCode ? "Dashboard.MySite.widget.code.hide" : "Dashboard.MySite.widget.code.show")}
            </button>
          </div>
          <button
            type="button"
            className={`${btnPrimaryClass} sm:min-w-56`}
            onClick={() => copy(snippet, "code")}
          >
            <i className={`bi ${copied === "code" ? "bi-check2-circle" : "bi-copy"}`} aria-hidden="true" />
            {copied === "code" ? t("Dashboard.MySite.widget.copied") : t("Dashboard.MySite.widget.copy")}
          </button>
        </div>
        {copied === "failed" && (
          <p className="mx-4 mt-0 mb-3 text-xs text-red-700 sm:mx-5" role="alert">
            {t("Dashboard.MySite.widget.copyFailed")}
          </p>
        )}
        {/* In the page even while folded: the copy button copies exactly this. */}
        <div id={`${ids}-code`} hidden={!showCode} className="border-t border-ink-200 px-4 py-4 sm:px-5">
          <pre className={codeBlockClass} data-testid="embed-snippet">
            <code>{snippet}</code>
          </pre>
          <p className={hintClass}>{t("Dashboard.MySite.widget.whereToPaste")}</p>
        </div>
      </section>
    </div>
  );
};

EmbedWidgetCard.propTypes = {
  /** The business's booking page address; empty until it has a name. */
  slug: PropTypes.string,
  /** The primary colour being edited: the floating button's. */
  primaryColor: PropTypes.string,
  /** The primary colour as saved: the one the form itself uses until a save. */
  savedColor: PropTypes.string,
  /** The booking page's own button text, if the business set one. */
  defaultLabel: PropTypes.string,
  /** Takes the business to where the colour is set. */
  onChangeColor: PropTypes.func,
};

export default EmbedWidgetCard;
